/**
 * findingsModel.ts
 *
 * Pure helpers for the Title Search Findings mode: turn the ordered
 * `titleSearch/timeline.ts` entries into display rows (date, type, type tag,
 * amount, parties, instrument number) and the property tax facts, using the
 * `titleSearchFindingsService` formatters and placeholder.
 *
 * No React and no network.
 *
 * Requirements: 10.1, 10.2, 10.3.
 */

import type { Job, PropertyTaxInformation, TaxInstallment } from '../../graphql/types';
import {
  PLACEHOLDER,
  displayOrPlaceholder,
  formatCurrency,
  formatDeedDate,
} from '../../services/titleSearchFindingsService';
import { buildTimeline, parseTimestamp } from '../../titleSearch/timeline';
import type { TimelineDocument, TimelineEntry } from '../../titleSearch/timeline';

export { PLACEHOLDER };

// ── Type tags ───────────────────────────────────────────────────────────────

export type FindingsTag = 'Conveyance' | 'Lien' | 'Release' | 'Assignment' | 'Subject';

/** Tag pill and timeline dot styling (status colours from the design system). */
export const TAG_STYLES: Record<FindingsTag, { pill: string; dot: string }> = {
  Conveyance: { pill: 'bg-[#EDE7FF] text-[#5A21C9]', dot: 'bg-[#834DFB]' },
  Lien: { pill: 'bg-[#fef9c3] text-[#854d0e]', dot: 'bg-[#eab308]' },
  Release: { pill: 'bg-[#dcfce7] text-[#166534]', dot: 'bg-[#16a34a]' },
  Assignment: { pill: 'bg-[#f3f4f6] text-[#4b5563]', dot: 'bg-[#d1d5db]' },
  Subject: { pill: 'bg-[#1c1428] text-white', dot: 'bg-[#1c1428]' },
};

/** Dot for a row whose document type has no tag. */
export const UNTAGGED_DOT = 'bg-[#d1d5db]';

/** The subject loan, as far as the job records it. */
export interface SubjectLoanRef {
  /** Recording reference of the subject mortgage (instrument or book/page). */
  recordingReference?: string | null;
  loanAmount?: number | string | null;
}

/**
 * Tag for a document type, or null when it fits none of the five.
 * Releases and assignments are matched before liens so that "Release of
 * Mortgage" and "Assignment of Deed of Trust" are not read as liens.
 */
export function deriveTypeTag(documentType: string | null | undefined): FindingsTag | null {
  const t = (documentType ?? '').toLowerCase();
  if (/release|satisf|reconvey|discharge/.test(t)) return 'Release';
  if (t.includes('assignment')) return 'Assignment';
  if (/mortgage|deed of trust|judg(e)?ment|lien/.test(t)) return 'Lien';
  if (t.includes('deed')) return 'Conveyance';
  return null;
}

function normalizeReference(value: string | null | undefined): string {
  return (value ?? '').replace(/\s+/g, '').toLowerCase();
}

function toAmountNumber(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const cleaned = value.replace(/[$,\s]/g, '');
  if (cleaned === '') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

/**
 * True when a lien-type document is the subject loan's own mortgage: its
 * instrument number equals the recorded subject reference, or its amount equals
 * the subject loan amount.
 */
export function isSubjectInstrument(
  row: { tag: FindingsTag | null; instrument: string | null; amountValue: number | null },
  subject: SubjectLoanRef | undefined
): boolean {
  if (row.tag !== 'Lien' || !subject) return false;
  const ref = normalizeReference(subject.recordingReference);
  if (ref !== '' && row.instrument && normalizeReference(row.instrument) === ref) return true;
  const loanAmount = toAmountNumber(subject.loanAmount);
  return loanAmount !== null && loanAmount > 0 && row.amountValue === loanAmount;
}

// ── Row extraction ──────────────────────────────────────────────────────────

export interface FindingsRow {
  documentId: string;
  /** "May 14, 2023", or the placeholder when the document has no date. */
  date: string;
  dateIsJudgment: boolean;
  documentType: string;
  tag: FindingsTag | null;
  /** Formatted amount, or null when the document has none. */
  amount: string | null;
  /** Numeric amount behind `amount`, when it is a number. */
  amountValue: number | null;
  /** "Grantor → Grantee" style text, or null. */
  parties: string | null;
  /** Instrument or book/page number, or null. */
  instrument: string | null;
}

type EntryField = TimelineEntry['extractedFields'][number];

const NOT_PARTY = /address|date|number|capacity|notary|signature|county|state|marital/;
const FROM_PARTY = /\b(?:grantor|mortgagor|borrower|assignor|defendant|debtor|releasor|obligor|trustor)s?\b/;
const TO_PARTY = /\b(?:grantee|mortgagee|lender|assignee|plaintiff|creditor|beneficiary)s?\b/;
const AMOUNT_FIELD = /amount|consideration|principal|sale price/;
const INSTRUMENT_FIELD = /instrument|book.*page|document number|recording number|reception/;

function valuesOf(field: EntryField): string[] {
  return field.values.length > 0 ? field.values : field.value !== '' ? [field.value] : [];
}

function partyText(fields: EntryField[], pattern: RegExp): string {
  const names: string[] = [];
  for (const f of fields) {
    const label = f.label.toLowerCase();
    if (NOT_PARTY.test(label) || !pattern.test(label)) continue;
    for (const v of valuesOf(f)) if (!names.includes(v)) names.push(v);
  }
  return names.join(', ');
}

/** "From → To" for the parties of a document; one side alone when only that is known. */
export function formatParties(fields: EntryField[]): string | null {
  const from = partyText(fields, FROM_PARTY);
  const to = partyText(fields, TO_PARTY);
  if (from && to) return `${from} → ${to}`;
  return from || to || null;
}

function findAmount(fields: EntryField[]): { text: string; value: number | null } | null {
  for (const f of fields) {
    const label = f.label.toLowerCase();
    if (!AMOUNT_FIELD.test(label) || label.includes('date') || f.value === '') continue;
    const value = toAmountNumber(f.values[0] ?? f.value);
    return value === null ? { text: f.value, value: null } : { text: formatCurrency(value), value };
  }
  return null;
}

function findInstrument(fields: EntryField[]): string | null {
  for (const f of fields) {
    if (INSTRUMENT_FIELD.test(f.label.toLowerCase()) && f.value !== '') return f.value;
  }
  return null;
}

/** Display row for one timeline entry. */
export function toFindingsRow(entry: TimelineEntry, subject?: SubjectLoanRef): FindingsRow {
  const amount = findAmount(entry.extractedFields);
  const instrument = findInstrument(entry.extractedFields);
  const base = deriveTypeTag(entry.documentType);
  const row = {
    tag: base,
    instrument,
    amountValue: amount?.value ?? null,
  };
  return {
    documentId: entry.documentId,
    date: entry.date !== null && entry.dateSource !== 'none' ? formatDeedDate(entry.date) : PLACEHOLDER,
    dateIsJudgment: entry.dateSource === 'judgment',
    documentType: entry.documentType,
    tag: isSubjectInstrument(row, subject) ? 'Subject' : base,
    amount: amount?.text ?? null,
    amountValue: row.amountValue,
    parties: formatParties(entry.extractedFields),
    instrument,
  };
}

/**
 * The findings rows, oldest first by recorded date (judgment date for
 * judgments). Ordering is `titleSearch/timeline.ts`'s; undated documents follow
 * the dated ones.
 */
export function buildFindingsRows(
  documents: TimelineDocument[],
  subject?: SubjectLoanRef
): FindingsRow[] {
  return buildTimeline(documents).map((entry) => toFindingsRow(entry, subject));
}

/** Subject loan reference for a TR job. */
export function subjectLoanOf(
  job: Pick<Job, 'subjectMortgageRecordingReference' | 'subjectLoanAmount'>
): SubjectLoanRef {
  return {
    recordingReference: job.subjectMortgageRecordingReference,
    loanAmount: job.subjectLoanAmount,
  };
}

/** "1 instrument" / "12 instruments". */
export function formatInstrumentCount(count: number): string {
  return `${count} ${count === 1 ? 'instrument' : 'instruments'}`;
}

// ── Property tax ────────────────────────────────────────────────────────────

export interface TaxFact {
  label: string;
  value: string;
  /** True when the value is the placeholder. */
  missing: boolean;
}

function isPaidInstallment(i: TaxInstallment): boolean {
  return (
    (typeof i.paidDate === 'string' && i.paidDate.trim() !== '') || /\bpaid\b/i.test(i.status ?? '')
  );
}

/** Due date of the earliest installment that has not been paid, formatted; placeholder if none. */
export function nextDueDate(info: PropertyTaxInformation | null | undefined): string {
  const installments = Array.isArray(info?.installments) ? info!.installments : [];
  let best: { ts: number; raw: string } | null = null;
  for (const i of installments) {
    if (isPaidInstallment(i)) continue;
    const ts = parseTimestamp(i.dueDate);
    if (ts !== null && (best === null || ts < best.ts)) best = { ts, raw: i.dueDate as string };
  }
  return best ? formatDeedDate(best.raw) : PLACEHOLDER;
}

/** Taxing units listed in the record's taxing entity (comma or semicolon separated). */
export function taxingUnitsOf(info: PropertyTaxInformation | null | undefined): string[] {
  return (info?.taxingEntity ?? '')
    .split(/[;,]/)
    .map((s) => s.trim())
    .filter((s) => s !== '');
}

/**
 * Property tax facts in display order. Values the record does not carry show
 * the placeholder. The record has no assessed value today, so that fact always
 * shows the placeholder until the backend provides it.
 */
export function buildTaxFacts(
  job: Pick<Job, 'apn' | 'propertyTaxInformation'>
): TaxFact[] {
  const info = job.propertyTaxInformation ?? null;
  const fact = (label: string, value: string): TaxFact => ({
    label,
    value,
    missing: value === PLACEHOLDER,
  });
  return [
    fact('APN', displayOrPlaceholder(job.apn)),
    fact('Tax year', displayOrPlaceholder(info?.taxYear)),
    fact('Assessed value', PLACEHOLDER),
    fact('Total tax', formatCurrency(info?.totalAnnualTax)),
    fact('Status', displayOrPlaceholder(info?.taxSaleStatus)),
    fact('Next due', nextDueDate(info)),
  ];
}
